import { Box, Typography } from "@mui/material";
import { useQuery } from "convex/react";
import { motion } from "framer-motion";
import React, { useCallback, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import useWindowSize from "../../../hooks/useWindowSize";
import { usePrefersReducedMotion } from "../../../hooks/useMotionPreferences";
import { lc, trackClick } from "../../_helpers";
import ProductCard from "../../merch/ProductCard";
import { GOLD, HAIRLINE_SOFT } from "../../merch/merchTheme";
import { lifestyleFor, useLifestyleImages } from "../../merch/useLifestyleImages";
import { isConvexConfigured } from "../../../lib/convex";
import { alsoAvailableLine, featuredItems, garmentSupport, type StoreListItem } from "../../../utils/merchStore";

const MAX_TILES = 4;

export type MerchSectionVariant = "section" | "inline";

interface MerchSectionProps {
  artistData?: { name?: string } | null;
  variant?: MerchSectionVariant;
  clickTarget?: string;
  allClickTarget?: string;
  lede?: string;
}

export function merchSectionEnabled(artistName: string | undefined | null, convexConfigured: boolean): boolean {
  return convexConfigured && lc(artistName || "") === "truevined";
}

export function MerchSection({
  artistData,
  variant = "section",
  clickTarget = "merch:artist-section",
  allClickTarget = "merch:artist-section-all",
  lede,
}: MerchSectionProps) {
  const windowSize = useWindowSize();
  const reducedMotion = usePrefersReducedMotion();
  const enabled = merchSectionEnabled(artistData?.name, isConvexConfigured);
  const inline = variant === "inline";

  const items = useQuery(api.merch.listStoreItems, enabled ? {} : "skip") as StoreListItem[] | undefined;
  const lifestyle = useLifestyleImages(enabled);

  const all = useMemo(() => items ?? [], [items]);
  const tiles = useMemo(() => featuredItems(all, MAX_TILES), [all]);
  const support = useMemo(() => garmentSupport(all), [all]);
  const lifestyleForItem = useCallback(
    (item: StoreListItem) => lifestyleFor(lifestyle, item.inks.map((ink) => ink.key), item.sku, item.defaultColor),
    [lifestyle],
  );

  if (!enabled || tiles.length === 0) return null;

  return (
    <Box
      component="section"
      className="MerchSection"
      aria-labelledby={`merch-heading-${variant}`}
      sx={{
        position: "relative",
        width: inline ? "100%" : `calc(${windowSize.width}px - (2.5rem * 2))`,
        p: inline ? { xs: "20px", md: "clamp(24px, 2.5rem, 44px)" } : { xs: "4rem 2rem", md: "3.5rem" },
        m: inline ? { xs: "4rem 0", md: "5rem 0" } : { xs: "1rem 2.5rem 0", md: "12.25rem 2.5rem 0" },
        backgroundColor: "var(--color5)",
        border: inline ? HAIRLINE_SOFT : undefined,
        borderRadius: inline ? "20px" : "1.875rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: { xs: "2.5rem", md: "2.5rem" },
        zIndex: 1,
      }}
    >
      <Box
        className="MerchHeader"
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: "2rem", md: "2.5rem" },
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id={`merch-heading-${variant}`}
            component="h2"
            sx={{ color: "var(--logoColor2)", fontSize: inline ? { xs: "3rem", md: "2.5rem" } : { xs: "3rem", md: "3.34rem" }, fontFamily: "Hanson", fontWeight: 700, m: 0 }}
          >
            MERCH
          </Typography>
          {lede ? (
            <Typography sx={{ mt: "8px", color: "var(--logoColor)", fontFamily: "Poppins, sans-serif", fontSize: { xs: "16px", md: "clamp(14px, 1rem, 17px)" }, opacity: 0.85 }}>
              {lede}
            </Typography>
          ) : null}
        </Box>
        <Box
          component={RouterLink}
          to="/merch"
          onClick={() => trackClick(allClickTarget, { label: inline ? "Bio page merch link" : "Artist page merch link", section: "merch" })}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: "44px",
            color: GOLD,
            fontSize: { xs: "16px", md: "clamp(15px, 1.25rem, 20px)" },
            fontFamily: "Poppins",
            fontWeight: 700,
            textDecoration: "none",
            transition: "opacity 0.2s ease",
            "&:hover": { opacity: 0.8 },
            "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "4px", borderRadius: "4px" },
          }}
        >
          See the whole store
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${tiles.length}, minmax(0, 1fr))` },
          gap: { xs: "2rem 1.5rem", md: "2.5rem" },
        }}
      >
        {tiles.map((item, index) => (
          <motion.div
            key={`${item.designSlug}:${item.sku}`}
            initial={reducedMotion ? false : { opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px 240px 0px" }}
            transition={{ duration: 0.5, delay: reducedMotion ? 0 : index * 0.08, ease: [0.23, 1, 0.32, 1] }}
            style={{ minWidth: 0 }}
          >
            <ProductCard
              item={item}
              alsoAs={alsoAvailableLine(support.get(item.designSlug), item.category)}
              lifestyle={lifestyleForItem(item)}
              clickTarget={clickTarget}
              showRelease
            />
          </motion.div>
        ))}
      </Box>
    </Box>
  );
}

export default MerchSection;
